import {Input} from '../../accountManagement/shared/Input'
import {Button} from '../../accountManagement/shared/Button'
import {Spinner as ButtonSpinner} from '../../accountManagement/shared/Button/Button.styles'
import {StyledSearchIcon} from '../../accountManagement/shared/icons/icons.styles'
import React, {
  useState,
  useRef,
  useEffect,
  useReducer,
  useMemo,
  useCallback,
} from 'react'
import {useAsync} from '../../utils/use-async'
import {useAuthClient} from '../../utils/use-auth-client'
import {toast} from 'react-toastify'
import {formatPrice} from '../../utils/currency-format'
import AddonItem from './AddonItem'
import SkeletonLoader from '../skeleton-loader'
import {useUserRoles} from '../../utils/use-user-roles'
import {useAuth0} from '@auth0/auth0-react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import ReactDOM from 'react-dom'
import {thryvLogo} from '../../assets'
import dayjs from 'dayjs'

const ITEMS_CONFIG = {
  'Thryv Leads': {
    isQuantityAdjustable: true,
    visibleOnlyOnPlans: [
      'plus',
      'professional',
      'unlimited',
      'sync',
      'thryvLeads',
      'marketingCenter',
    ],
  },
  'Thryv CSN': {},
  'SEO Non-Guaranteed': {},
  'SEO Money Back Guarantee': {},
  'AEO & SEO': {
    visibleOnlyOnPlans: [
      'plus',
      'professional',
      'unlimited',
      'sync',
      'marketingCenter',
    ],
    isQuantityAdjustable: true,
    childrenNames: ['AEO & SEO PowerBoost', 'SEO Keyword'],
  },
  'AEO & SEO PowerBoost': {
    isQuantityAdjustable: true,
    maxQuantityByPlan: {
      plus: 3,
      professional: 3,
      unlimited: 3,
      sync: 3,
    },
  },
  'SEO Keyword': {
    isQuantityAdjustable: true,
    maxQuantityByPlan: {
      plus: 3,
      professional: 3,
      unlimited: 3,
      sync: 3,
    },
  },
  'Enhanced Accessibility': {},
  'Restricted Access': {
    visibleOnlyOnPlans: ['plus', 'professional', 'unlimited'],
  },
  'Certified Domain Based Gmails': {
    isQuantityAdjustable: true,
    visibleOnlyOnPlans: ['plus', 'professional', 'unlimited', 'sync'],
    maxQuantityByPlan: {
      plus: 2,
      professional: 5,
      unlimited: 10,
    },
  },
  'Additional Social Boosting': {},
  'Additional Video Views': {},
  'Marketing Center Plus': {
    visibleOnlyOnPlans: [
      'plus',
      'professional',
      'unlimited',
      'sync',
      'marketingCenter',
    ],
  },
  'Marketing Center Foundation': {},
  'Marketing Center Pro': {
    visibleOnlyOnPlans: [
      'plus',
      'professional',
      'unlimited',
      'sync',
      'marketingCenter',
    ],
    disableItems: ['addon-4-mc-m2m'],
  },
  'Thryv Signature + Expanded Listings': {},
  'Starter Growth Package': {
    visibleOnlyOnPlans: [
      'plus',
      'professional',
      'unlimited',
      'sync',
      'marketingCenter',
    ],
  },
  'Expanded Growth Package': {
    visibleOnlyOnPlans: [
      'plus',
      'professional',
      'unlimited',
      'sync',
      'marketingCenter',
    ],
  },
  Website: {
    visibleOnlyOnPlans: ['plus', 'professional', 'unlimited', 'sync'],
  },
  HIPAA: {},
  'Marketing Pro Editor': {
    visibleOnlyOnPlans: ['plus', 'professional', 'unlimited'],
  },
  'Thryv Professional Services': {},
  'Social Ads Bronze': {
    visibleOnlyOnPlans: [
      'plus',
      'professional',
      'unlimited',
      'marketingCenter',
      'sync',
    ],
    isQuantityAdjustable: true,
    maxQuantityByPlan: {
      plus: 5,
      professional: 5,
      unlimited: 5,
      sync: 5,
      marketingCenter: 5,
    },
    quantityCanBeReset: true,
  },
  'Social Ads Silver': {
    visibleOnlyOnPlans: [
      'plus',
      'professional',
      'unlimited',
      'marketingCenter',
      'sync',
    ],
    isQuantityAdjustable: true,
    maxQuantityByPlan: {
      plus: 5,
      professional: 5,
      unlimited: 5,
      sync: 5,
      marketingCenter: 5,
    },
    quantityCanBeReset: true,
  },
  'Social Ads Gold': {
    visibleOnlyOnPlans: [
      'plus',
      'professional',
      'unlimited',
      'marketingCenter',
      'sync',
    ],
    isQuantityAdjustable: true,
    maxQuantityByPlan: {
      plus: 5,
      professional: 5,
      unlimited: 5,
      sync: 5,
      marketingCenter: 5,
    },
    quantityCanBeReset: true,
  },
  'Standard Domain Based Gmail': {
    visibleOnlyOnPlans: ['plus', 'professional', 'unlimited', 'sync'],
    isQuantityAdjustable: true,
    maxQuantityByPlan: {
      plus: 2,
      professional: 5,
      unlimited: 10,
      sync: 2,
      marketingCenter: 2,
    },
  },
  '5 Extra Website Pages': {
    isQuantityAdjustable: true,
    visibleOnlyOnPlans: ['plus', 'professional', 'unlimited', 'sync'],
    maxQuantityByPlan: {
      plus: 50,
      professional: 50,
      unlimited: 50,
      sync: 50,
      marketingCenter: 50,
    },
    // minQuantityIncrement: 5,
    displayMultiplier: 5,
  },
  Logo: {
    isQuantityAdjustable: true,
    visibleOnlyOnPlans: ['plus', 'professional', 'unlimited', 'sync'],
    maxQuantityByPlan: {
      plus: 100,
      professional: 100,
      unlimited: 100,
    },
  },
  'Google My Business Optimization': {},
  'Review Acknowledgment': {
    visibleOnlyOnPlans: ['professional', 'unlimited'],
  },
  'Signatures Business Center Unlimited': {
    visibleOnlyOnPlans: ['unlimited'],
  },
  'Social Content Generation': {
    visibleOnlyOnPlans: ['unlimited', 'professional'],
  },
  'Leads Lift': {
    visibleOnlyOnPlans: ['plus', 'professional', 'unlimited', 'sync'],
    isQuantityAdjustable: true,
  },
  'Thryv Social Posts': {
    visibleOnlyOnPlans: ['plus', 'professional', 'sync'],
  },
  'Strategic Content Creation': {
    visibleOnlyOnPlans: ['plus', 'professional', 'unlimited', 'sync'],
  },
  'Listing Refresh': {
    visibleOnlyOnPlans: ['plus', 'professional', 'unlimited', 'sync'],
  },
}

const AVAILABLE_COUNTRIES = [
  {name: 'United States', value: 'US', currency: 'USD'},
  {name: 'Australia', value: 'AU', currency: 'AUD'},
  {name: 'Cayman Islands', value: 'KY', currency: 'KYD'},
  {name: 'Barbados', value: 'BB', currency: 'BBD'},
  {name: 'Canada', value: 'CA', currency: 'CAD'},
  // {name: 'Great Britain', value: 'GB', currency: 'GBP'},
]

const DEFAULT_PLANS = ['plus', 'professional', 'unlimited']
const DEFAULT_BUNDLE_COUPONS = [
  {
    code: 'kickstart-bundle-10off',
    incompatibles: ['ignite-bundle-15off', 'accelerate-bundle-20off'],
  },
  {
    code: 'ignite-bundle-15off',
    incompatibles: ['kickstart-bundle-10off', 'accelerate-bundle-20off'],
  },
  {
    code: 'accelerate-bundle-20off',
    incompatibles: ['kickstart-bundle-10off', 'ignite-bundle-15off'],
  },
]

const productsReducer = (state, action) => {
  switch (action.type) {
    case 'RESET':
      return []
    case 'SET':
      return action.payload
    case 'ADD': {
      return [...state, action.payload]
    }
    case 'REMOVE':
      return state.filter(
        product => product.addOnCode !== action.payload.addOnCode,
      )
    case 'UPDATE_QUANTITY': {
      const {addOnCode, quantity} = action.payload
      return state.map(p => (p.addOnCode === addOnCode ? {...p, quantity} : p))
    }
    case 'INCREMENT': {
      const {addOnCode} = action.payload
      return state.map(p =>
        p.addOnCode === addOnCode ? {...p, quantity: p.quantity + 1} : p,
      )
    }
    case 'DECREMENT': {
      const {addOnCode} = action.payload
      return state.map(p =>
        p.addOnCode === addOnCode
          ? {...p, quantity: Math.max(1, p.quantity - 1)}
          : p,
      )
    }
    default:
      return state
  }
}

const ADDON_CATEGORIES = {
  'Marketing Center Plus': 1,
  'Marketing Center Foundation': 1,
  'Marketing Center Pro': 1,
  'Thryv Signature + Expanded Listings': 1,
  'Thryv Leads': 2,
  'Leads Lift': 2,
  'SEO Non-Guaranteed': 3,
  'SEO Money Back Guarantee': 3,
  'AEO & SEO': 3,
  'AEO & SEO PowerBoost': 3,
  'SEO Keyword': 3,
  'Starter Growth Package': 4,
  'Expanded Growth Package': 4,
  'Social Ads Bronze': 5,
  'Social Ads Silver': 6,
  'Social Ads Gold': 7,
  'Additional Social Boosting': 5,
  'Additional Video Views': 5,
  'Marketing Pro Editor': 8,
  Website: 8,
  'Restricted Access': 9,
  'Standard Domain Based Gmail': 10,
  'Certified Domain Based Gmails': 11,
}

const sortAddons = (a, b) => {
  const categoryA = ADDON_CATEGORIES[a.name] || 999
  const categoryB = ADDON_CATEGORIES[b.name] || 999

  if (categoryA === categoryB) {
    return a.name.localeCompare(b.name)
  }

  return categoryA - categoryB
}

function PriceCalculatorScreen() {
  const [email, setEmail] = React.useState('')
  const [accountVersion, setAccountVersion] = useState(null)
  const [addonsData, setAddonsData] = useState(null)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [isLoadingAddons, setIsLoadingAddons] = useState(false)
  const [isLoadingAccount, setIsLoadingAccount] = useState(false)
  const [billingPreference] = useState('monthly')
  const [couponSearchTerm, setCouponSearchTerm] = useState('')
  const [coupons, setCoupons] = useState([])
  const [selectedCoupons, setSelectedCoupons] = useState([])
  const [paymentPreview, setPaymentPreview] = useState(null)
  const [isLoadingPaymentPreview, setIsLoadingPaymentPreview] = useState(false)
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false)
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false)
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false)
  const [selectedItems, dispatch] = useReducer(productsReducer, [])
  const [planCodeNames, setPlanCodeNames] = useState([])
  const [lineItemsToLoad, setLineItemsToLoad] = useState([])
  const [userOnboardingFeeItem, setUserOnboardingFee] = useState(null)
  const accountSubscriptionRef = useRef('')
  const plansPricesInfoRef = useRef()
  const lastCountrySearchedRef = useRef()
  const defaultAddedProducts = useRef([])
  const removedIncompatibleProducts = useRef([])
  const addonsDataRef = useRef()
  const thryvIdRef = useRef()
  const printableElementRef = useRef()
  const {run} = useAsync()
  const client = useAuthClient()
  const {roles} = useUserRoles()
  const {user} = useAuth0()

  const validateCoupon = async ({
    couponCode,
    couponPlanCode,
    country,
    addonsString = '',
  }) => {
    try {
      let url = `orders/coupon/validate?couponCode=${couponCode}&country=${country}`

      if (couponPlanCode) {
        url += `&planCode=${couponPlanCode}`
      }

      if (addonsString) {
        url += `&addonsString=${addonsString}`
      }

      const response = await client(url)

      return response
    } catch (error) {
      console.error(`Failed to validate coupon: ${error.message}`)
      return null
    }
  }

  const getPlanInfoByCountry = async countryCode => {
    try {
      const plansByCountry = await client(`plans/prices/countries`)

      const planInfo = plansByCountry?.[countryCode]
      return planInfo
    } catch (error) {
      toast.error(`Failed to fetch plans information`)
      return null
    }
  }

  const addNonDuplicatedCoupon = coupon => {
    setCoupons(prevCoupons => {
      const isDuplicate = prevCoupons.some(c => c.code === coupon.code)
      if (!isDuplicate) {
        return [coupon, ...prevCoupons]
      }
      return prevCoupons
    })
  }

  const handleSearchCouponCode = async () => {
    setIsLoadingCoupons(true)
    const addonsString = selectedItems.map(item => item.addOnCode).join(',')

    if (coupons.some(coupon => coupon.code === couponSearchTerm)) {
      toast.info('This coupon is already added')
      setIsLoadingCoupons(false)
      return
    }

    const validatedCoupon = await validateCoupon({
      country: lastCountrySearchedRef.current.value,
      couponCode: couponSearchTerm,
      addonsString,
    })

    setIsLoadingCoupons(false)

    if (validatedCoupon?.isValid) {
      setCouponSearchTerm('')
      addNonDuplicatedCoupon(validatedCoupon)
    }

    if (!validatedCoupon?.isValid) {
      toast.error(
        `This coupon is not a valid coupon for the items added to the cart.`,
      )
    }
  }

  const validateDefaultCoupons = async items => {
    if (!lastCountrySearchedRef.current || !items?.length) return

    setIsLoadingCoupons(true)
    const addonsString = items.map(item => item.addOnCode).join(',')

    for (const coupon of DEFAULT_BUNDLE_COUPONS) {
      if (coupons.some(c => c.code === coupon.code)) continue

      const validatedCoupon = await validateCoupon({
        country: lastCountrySearchedRef.current.value,
        couponCode: coupon.code,
        addonsString,
      })

      if (validatedCoupon?.isValid) {
        addNonDuplicatedCoupon(validatedCoupon)
      }
    }
    setIsLoadingCoupons(false)
  }

  const handleEmailValidation = async () => {
    setUserOnboardingFee(null)

    const emailPatternRole =
      roles.includes('keappartner') ||
      roles.includes('reseller') ||
      roles.includes('admin') ||
      roles.includes('KeapPartner') ||
      roles.includes('Reseller') ||
      roles.includes('Admin')
    let emailRegex = /^(?!.*\+)[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (emailPatternRole) {
      emailRegex = /^[a-zA-Z0-9._+-]+@[^\s@]+\.[^\s@]+$/
    }
    if (emailRegex.test(email)) {
      const isThryvEmail = email.includes('@thryv.com')
      if (isThryvEmail) {
        toast.error('Thryv addresses are not allowed')
        return
      }

      try {
        setIsLoadingAccount(true)
        const response = await run(client(`orders/validate-email/${email}`))

        thryvIdRef.current = response.thryvId
        const isInvoiceBilled = response.isInvoiceBilled

        if (isInvoiceBilled) {
          const {openInvoices} = await client(`invoices/${response.thryvId}`)
          if (openInvoices?.length > 0) {
            return toast.error(
              'This account has open invoices, and cannot be used in the calculator.',
            )
          }
        }

        if (response.v5upgrade === undefined) {
          setAccountVersion(null)
          return toast.warning(
            'This account was not found. Please ensure you are using an active Thryv 4.0 subscription.',
          )
        }
        if (response.v5upgrade) {
          setAccountVersion('v5.0')
          return toast.warn(
            'Oops! This calculator is just for our 4.0 clients. For 5.0 pricing, please use the order process instead. Thank you!',
          )
        }

        try {
          const subscriptionId = `uuid-${response.subscriptionId}`

          const subscriptionResponse = await run(
            client(`subscription/${subscriptionId}`),
          )

          const selectedCountry = AVAILABLE_COUNTRIES.find(c => {
            return c.value === subscriptionResponse.billingAddress.country
          })

          if (!selectedCountry) {
            toast.error('No billing country found for this account')
            return
          }

          const plansListByCountry = await getPlanInfoByCountry(
            selectedCountry.value,
          )

          const clientPlanName = subscriptionResponse.planName.toLowerCase()

          const isPlanValidForSelectedCountry = plansListByCountry[
            billingPreference
          ]?.some(
            planInfo => planInfo.planName.toLowerCase() === clientPlanName,
          )

          if (!isPlanValidForSelectedCountry) {
            toast.error(
              'The plan of this account is not valid for the selected country.',
            )
            return
          }

          if (!DEFAULT_PLANS.includes(clientPlanName)) {
            setPlanCodeNames([clientPlanName, ...DEFAULT_PLANS])
          } else {
            setPlanCodeNames(DEFAULT_PLANS)
          }

          lastCountrySearchedRef.current = selectedCountry
          plansPricesInfoRef.current = plansListByCountry?.[billingPreference]

          // check if the user has another price for his active plan and set the correct price
          plansPricesInfoRef.current = plansPricesInfoRef.current.map(
            planPriceInfo => {
              if (
                planPriceInfo.planName.toLowerCase() ===
                subscriptionResponse.planName.toLowerCase()
              ) {
                return {
                  ...planPriceInfo,
                  amount:
                    subscriptionResponse.planPrice !== undefined &&
                    subscriptionResponse.planPrice !== null
                      ? subscriptionResponse.planPrice
                      : planPriceInfo.amount,
                }
              }
              return planPriceInfo
            },
          )

          defaultAddedProducts.current = subscriptionResponse.addOns
          accountSubscriptionRef.current = subscriptionResponse
          await handleLoadPlan(clientPlanName)
        } catch (subscriptionError) {
          console.error('Failed to fetch subscription data.', subscriptionError)
          return toast.error(
            'Failed to fetch subscription data.',
            subscriptionError,
          )
        }

        return setAccountVersion('v4.0')
      } catch (err) {
        return toast.error(err.message)
      } finally {
        setIsLoadingAccount(false)
      }
    } else {
      return toast.error('Valid email address required')
    }
  }

  const handleLoadPlan = async planType => {
    const planName = planType.toLowerCase()

    try {
      setUserOnboardingFee(null)
      setIsLoadingAddons(true)
      setSelectedPlan(planType)
      setAddonsData(null)
      dispatch({type: 'RESET'})
      const response = await run(client(`plans/list/addons/${planName}`))
      addonsDataRef.current = response || null

      if (defaultAddedProducts.current.length > 0) {
        const selectedPlanInfo = plansPricesInfoRef.current?.find(
          planInfo => planInfo.name === planName,
        )

        const planPayload = {
          addOnCode: selectedPlanInfo.planCode,
          name: `${selectedPlanInfo.planName}`,
          quantity: 1,
          unitAmount: selectedPlanInfo.amount,
          type: 'plan',
        }

        const newItems = [...defaultAddedProducts.current, planPayload]

        dispatch({
          type: 'SET',
          payload: newItems,
        })

        // Validate default coupons with the complete items array
        const allAvailableProducts = response?.addOns
          ?.map(addon => ({
            addOnCode: addon[billingPreference][0]?.code,
            name: addon.name,
            quantity: 1,
            unitAmount:
              addon[billingPreference][0]?.currencies?.[0]?.unitAmount,
          }))
          .concat(
            response?.oneTimeAddOns?.map(addon => ({
              addOnCode: addon.days?.code,
              name: addon.name,
              quantity: 1,
              unitAmount: addon.days?.currencies?.[0]?.unitAmount,
            })),
          )
          .filter(product => product.addOnCode)

        await validateDefaultCoupons([...allAvailableProducts, planPayload])
      }

      setAddonsData(response || null)
    } catch (err) {
      toast.error(`Failed to fetch ${planType} addons: ${err.message}`)
      setAddonsData(null)
    } finally {
      setIsLoadingAddons(false)
    }
  }

  const incompatibleAddons = selectedItems
    .filter(item => {
      return ITEMS_CONFIG?.[item?.name]?.disableItems
    })
    .flatMap(item =>
      (ITEMS_CONFIG?.[item?.name]?.disableItems || []).map(
        disabledItem => disabledItem,
      ),
    )
  const isAddonIncompatible = addOnCode => {
    return incompatibleAddons.includes(addOnCode)
  }

  const handleCheckOnItem = ({event, summaryInfo}) => {
    const {checked} = event.target
    const addonConfig = ITEMS_CONFIG[summaryInfo.name] || {}

    if (isAddonDefaultAdded(summaryInfo.code)) return

    if (checked) {
      if (addonConfig.disableItems && addonConfig.disableItems.length > 0) {
        const incompatibleAddons = [...selectedItems].filter(item =>
          addonConfig.disableItems.includes(item.addOnCode),
        )

        // Remove incompatible addons
        if (incompatibleAddons.length > 0) {
          const newSelectedItems = selectedItems.filter(item => {
            return !addonConfig.disableItems.includes(item.addOnCode)
          })

          removedIncompatibleProducts.current = incompatibleAddons

          dispatch({
            type: 'SET',
            payload: [...newSelectedItems, summaryInfo],
          })
          return
        }
      }

      dispatch({type: 'ADD', payload: summaryInfo})
    } else {
      // if this addon was deactivating a default added addon, reactivate those default addons
      const defaultAddonsToReactivate =
        removedIncompatibleProducts.current?.filter(item => {
          return addonConfig?.disableItems?.includes(item.addOnCode)
        })

      if (defaultAddonsToReactivate && defaultAddonsToReactivate.length > 0) {
        const newPayloadsToAdd = defaultAddonsToReactivate.map(item => {
          return {
            addOnCode: item.addOnCode,
            name: item.name,
            quantity: item.quantity,
            unitAmount: item.unitAmount,
          }
        })
        dispatch({
          type: 'SET',
          payload: [...newPayloadsToAdd, ...selectedItems],
        })
      }

      dispatch({
        type: 'REMOVE',
        payload: {addOnCode: summaryInfo.addOnCode},
      })
    }
  }

  const handleCheckOnCoupon = ({event, coupon}) => {
    const {checked} = event.target

    // Remove incompatible coupons when checking a default bundle coupon
    if (checked) {
      const isDefaultCoupon = DEFAULT_BUNDLE_COUPONS.some(
        defaultCoupon => defaultCoupon.code === coupon.code,
      )

      if (isDefaultCoupon) {
        const incompatibleCouponCodes = DEFAULT_BUNDLE_COUPONS.find(
          defaultCoupon => defaultCoupon.code === coupon.code,
        ).incompatibles

        if (incompatibleCouponCodes.length > 0) {
          const newSelectedCouponsWithoutIncompatibles = selectedCoupons.filter(
            selectedCoupon =>
              !incompatibleCouponCodes.includes(selectedCoupon.code),
          )

          return setSelectedCoupons([
            ...newSelectedCouponsWithoutIncompatibles,
            coupon,
          ])
        }
      }
      setSelectedCoupons(prev => [...prev, coupon])
    } else {
      setSelectedCoupons(prev => prev.filter(c => c.code !== coupon.code))
    }
  }

  const isQuantityAdjustable = itemName => {
    return ITEMS_CONFIG[itemName]?.isQuantityAdjustable || false
  }

  // Helper function to check if an addon is visible on the current selected plan, if is not part of a default plan, so it will be showed
  const isAddonVisibleOnSelectedPlan = (addonName, currentPlan) => {
    const itemConfig = ITEMS_CONFIG[addonName]
    return itemConfig?.visibleOnlyOnPlans?.includes(currentPlan)
  }

  const handleQuantityChange = (addOnCode, value, name) => {
    let newQuantity = Number(value)
    if (isNaN(newQuantity) || newQuantity < 0) {
      newQuantity = 1
    }
    newQuantity = Math.floor(newQuantity)

    const minQuantityIncrement =
      Number(ITEMS_CONFIG?.[name]?.minQuantityIncrement) || 1
    const maxQuantity =
      Number(ITEMS_CONFIG?.[name]?.maxQuantityByPlan?.[selectedPlan]) ||
      Infinity
    const defaultProduct = isAddonDefaultAdded(addOnCode)
    const minAllowedQuantity = defaultProduct?.isDefault
      ? Number(defaultProduct.quantity) || 1
      : 1

    // Validate minimum quantity
    if (newQuantity < minAllowedQuantity) {
      dispatch({
        type: 'UPDATE_QUANTITY',
        payload: {addOnCode, quantity: minAllowedQuantity},
      })
      return toast.info(
        `The minimum quantity for this item is ${minAllowedQuantity}`,
      )
    }

    if (minQuantityIncrement && value % minQuantityIncrement !== 0) {
      toast.info(`Quantity must be a multiple of ${minQuantityIncrement}`)
      dispatch({
        type: 'UPDATE_QUANTITY',
        payload: {addOnCode, quantity: minQuantityIncrement},
      })
      return
    }

    // Validate maximum quantity
    if (newQuantity > maxQuantity) {
      dispatch({
        type: 'UPDATE_QUANTITY',
        payload: {addOnCode, quantity: maxQuantity},
      })
      return toast.info(`Maximum quantity is ${maxQuantity}`)
    }

    // If all validations pass, update with the new quantity
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: {addOnCode, quantity: newQuantity},
    })
  }

  const handleIncrement = (addOnCode, name, quantity) => {
    // Ensure quantity is a number
    const currentQuantity = Number(quantity) || 0
    const minQuantityIncrement =
      Number(ITEMS_CONFIG?.[name]?.minQuantityIncrement) || 1
    const maxQuantity =
      Number(ITEMS_CONFIG?.[name]?.maxQuantityByPlan?.[selectedPlan]) ||
      Infinity

    if (maxQuantity && currentQuantity >= maxQuantity) {
      return toast.info(`Maximum quantity of ${maxQuantity} reached`)
    }

    if (minQuantityIncrement > 1) {
      const newQuantity = Math.min(
        currentQuantity + minQuantityIncrement,
        maxQuantity,
      )
      return handleQuantityChange(addOnCode, newQuantity, name)
    }

    // For single increments, just use the reducer
    dispatch({type: 'INCREMENT', payload: {addOnCode}})
  }

  const handleDecrement = (addOnCode, name, quantity) => {
    // Ensure quantity is a number
    const currentQuantity = Number(quantity) || 0
    const minQuantityIncrement =
      Number(ITEMS_CONFIG?.[name]?.minQuantityIncrement) || 1
    const defaultProduct = isAddonDefaultAdded(addOnCode)
    const minAllowedQuantity = defaultProduct?.isDefault
      ? Number(defaultProduct.quantity) || 1
      : minQuantityIncrement
    const quantityCanBeReset = ITEMS_CONFIG?.[name]?.quantityCanBeReset || false

    if (currentQuantity <= minAllowedQuantity && !quantityCanBeReset) {
      return toast.info(`Minimum quantity is ${minAllowedQuantity}`)
    }

    if (minQuantityIncrement > 1) {
      const newQuantity = Math.max(
        currentQuantity - minQuantityIncrement,
        minAllowedQuantity,
      )
      return handleQuantityChange(addOnCode, newQuantity, name)
    }

    // For single decrements, just use the reducer
    dispatch({type: 'DECREMENT', payload: {addOnCode}})
  }

  const isAddonDefaultAdded = addOnCode => {
    if (!Array.isArray(defaultAddedProducts.current)) return null
    const defaultProduct = defaultAddedProducts.current.find(
      a => a.addOnCode === addOnCode,
    )
    return defaultProduct
      ? {
          isDefault: true,
          quantity: defaultProduct.quantity || 1,
        }
      : null
  }

  const firstMonthTotal = selectedItems.reduce((total, product) => {
    return total + product.unitAmount * product.quantity
  }, 0)

  const subtotal = selectedItems.reduce((total, product) => {
    return total + product.unitAmount * product.quantity
  }, 0)

  const calculateTotalDiscount = () => {
    if (!selectedCoupons || selectedCoupons.length === 0) return 0

    let totalDiscount = 0
    for (const coupon of selectedCoupons) {
      // Check if coupon applies to any of the selected items
      const couponAppliesToSomeItems = selectedItems.some(
        item =>
          coupon.appliesToPlans?.includes(item.addOnCode) ||
          coupon.appliesToItems?.includes(item.addOnCode),
      )

      // Check if coupon applies to all plans
      const appliesToAll =
        coupon.appliesToAllPlans === true &&
        !coupon.appliesToPlans?.length > 0 &&
        !coupon.appliesToItems?.length > 0

      if (couponAppliesToSomeItems || appliesToAll) {
        if (coupon.discountAmount > 0) {
          totalDiscount += coupon.discountAmount
        } else if (coupon.discountPercent > 0) {
          const itemTotal = selectedItems.reduce((total, item) => {
            if (coupon.appliesToPlans?.includes(item.addOnCode)) {
              return total + item.unitAmount * item.quantity
            }
            return total
          }, 0)
          totalDiscount += itemTotal * (coupon.discountPercent / 100)
        }
      }
    }

    return totalDiscount
  }

  useEffect(() => {
    setPaymentPreview(null)
    const timer = setTimeout(async () => {
      const loadPaymentPreview = async () => {
        setUserOnboardingFee(null)

        const subscriptionsToCheck = selectedItems
          .filter(
            item =>
              item.type !== 'plan' &&
              !defaultAddedProducts.current.find(
                p => p.addOnCode === item.addOnCode,
              ),
          )
          .map(item => ({
            planCode: item.addOnCode,
            quantity: item.quantity,
          }))

        // If the selected plan is different from the account's current plan,
        // include it as a new subscription so the backend can calculate the
        // prorated amount for the plan change.
        if (selectedPlan && plansPricesInfoRef.current) {
          const selectedPlanInfo = plansPricesInfoRef.current.find(
            pi => pi.name?.toLowerCase() === selectedPlan.toLowerCase(),
          )

          const selectedPlanCode = selectedPlanInfo?.planCode

          if (
            selectedPlanCode &&
            accountSubscriptionRef.current?.planCode !== selectedPlanCode
          ) {
            // Avoid duplicating if the plan was already present in subscriptionsToCheck
            const alreadyIncluded = subscriptionsToCheck.some(
              s => s.planCode === selectedPlanCode,
            )

            if (!alreadyIncluded) {
              subscriptionsToCheck.unshift({
                planCode: selectedPlanCode,
                quantity: 1,
              })
            }
          }
        }

        const oldSubscriptionsToCheck = selectedItems
          .filter(item => {
            const defaultProduct = defaultAddedProducts.current.find(
              p => p.addOnCode === item.addOnCode,
            )
            return defaultProduct && item.quantity > defaultProduct.quantity
          })
          .map(item => {
            const defaultProduct = defaultAddedProducts.current.find(
              p => p.addOnCode === item.addOnCode,
            )
            return {
              subscriptionId: 'uuid-' + defaultProduct.uuid,
              body: {
                planCode: item.addOnCode,
                quantity: item.quantity,
              },
            }
          })

        setLineItemsToLoad([
          ...subscriptionsToCheck,
          ...oldSubscriptionsToCheck,
        ])

        try {
          const requestData = {
            account: {
              code: thryvIdRef.current,
            },
            currency: lastCountrySearchedRef.current?.currency,
            newSubscriptions: subscriptionsToCheck,
            oldSubscriptions: oldSubscriptionsToCheck,
            coupons: selectedCoupons.map(coupon => coupon.code),
          }

          let paymentPreviewResponse

          if (
            oldSubscriptionsToCheck.length > 0 ||
            subscriptionsToCheck.length > 0
          ) {
            setIsLoadingPaymentPreview(true)

            paymentPreviewResponse = await client(
              'cc/order/all-plans-payment-preview',
              {
                data: requestData,
              },
            )
          }

          if (paymentPreviewResponse?.status === 400) {
            const errorMessage =
              paymentPreviewResponse.message?.[0]?.message || 'Invalid request'
            toast.error(errorMessage)
            return
          }

          let newSubscriptionsInvoiceLineItems = []
          let oldSubscriptionsPreviewInvoicesLineItems = []
          let totalDueToday = 0

          if (paymentPreviewResponse?.newSubscriptionsPurchasePreview) {
            newSubscriptionsInvoiceLineItems =
              paymentPreviewResponse.newSubscriptionsPurchasePreview?.invoice.chargeInvoice.lineItems.map(
                lineItem => {
                  return {
                    description: lineItem.description,
                    quantity: lineItem.quantity,
                    amount: lineItem.amount,
                    planCode: lineItem.planCode,
                  }
                },
              )

            totalDueToday +=
              paymentPreviewResponse.newSubscriptionsPurchasePreview.invoice
                .chargeInvoice.total
          }

          if (paymentPreviewResponse?.oldSubscriptionsPurchasePreview) {
            oldSubscriptionsPreviewInvoicesLineItems =
              paymentPreviewResponse.oldSubscriptionsPurchasePreview.map(
                updatePreview => {
                  const chargeInvoiceLineItem =
                    updatePreview.invoiceCollection.chargeInvoice.lineItems?.[0]

                  totalDueToday += chargeInvoiceLineItem.amount

                  return {
                    description: chargeInvoiceLineItem.description,
                    quantity: chargeInvoiceLineItem.quantity,
                    amount: chargeInvoiceLineItem.amount,
                    planCode: chargeInvoiceLineItem.planCode,
                  }
                },
              )
          }

          const paymentPreview = {
            totalDueToday,
            lineItems: [
              ...newSubscriptionsInvoiceLineItems,
              ...oldSubscriptionsPreviewInvoicesLineItems,
            ],
          }

          const isSyncPlan =
            accountSubscriptionRef.current?.planCode?.includes('sync')

          const hasMarketingCenterAddOn = selectedItems.some(item =>
            item.name.toLowerCase().includes('marketing center'),
          )

          // check if user needs to pay onBoarding fee and charge the fee if needed
          if (
            (!accountSubscriptionRef.current?.hasPaidOnboardingFee &&
              isSyncPlan &&
              selectedPlan !== 'sync') ||
            (isSyncPlan &&
              hasMarketingCenterAddOn &&
              !accountSubscriptionRef.current?.hasPaidOnboardingFee)
          ) {
            const selectedPlanInfo = plansPricesInfoRef.current?.find(
              planInfo =>
                planInfo.name.toLowerCase() === selectedPlan.toLowerCase(),
            )

            const onBoardingFeeAmount = parseInt(selectedPlanInfo?.onboarding)

            paymentPreview.totalDueToday += onBoardingFeeAmount
            paymentPreview.lineItems = [
              ...paymentPreview.lineItems,
              {
                description: 'Onboarding Fee',
                quantity: 1,
                amount: onBoardingFeeAmount,
                planCode: 'onboarding-fee',
              },
            ]
            setUserOnboardingFee({
              description: 'Onboarding Fee',
              quantity: 1,
              amount: onBoardingFeeAmount,
              planCode: 'onboarding-fee',
            })
          } else {
            setUserOnboardingFee(null)
          }

          setPaymentPreview(paymentPreview)
        } catch (error) {
          console.error('Error fetching payment preview:', error)
          const errorMessage =
            error.response?.message?.[0]?.message ||
            'Failed to calculate total due today'
          toast.error(errorMessage)
        } finally {
          setIsLoadingPaymentPreview(false)
        }
      }

      loadPaymentPreview()
    }, 500)

    return () => clearTimeout(timer)
  }, [selectedCoupons, selectedItems, client, selectedPlan, billingPreference])

  const parseAddon = useCallback(
    addOn => {
      const addonInfo = addOn[billingPreference][0]
      const itemConfigInfo = ITEMS_CONFIG?.[addOn.name] || {}

      const selectedCurrencyInfo = addonInfo.currencies?.find(
        currency =>
          currency.currency === lastCountrySearchedRef.current.currency,
      )

      const minQuantityIncrement = itemConfigInfo.minQuantityIncrement || 1

      // get the addon currency info (important info like the price and so)
      const addonCurrency = addOn[billingPreference]?.[0]

      // parse info for the cart selection
      const summaryInfo = {
        addOnCode: addonInfo.code,
        name: addOn.name,
        quantity: minQuantityIncrement,
        unitAmount: selectedCurrencyInfo?.unitAmount,
      }

      // check if it is an already added to the order product
      const defaultProduct = isAddonDefaultAdded(addonCurrency?.code)
      const isDefaultAdded = !!defaultProduct?.isDefault

      // check if the item needs to be disable because of incompatibility or another reason
      const isIncompatible = isAddonIncompatible(addonCurrency?.code)

      // check if the addon is visible on the current selected plan, if is not part of a default plan, so it will be showed
      const selectedItem = selectedItems.find(
        item => item.addOnCode === addonCurrency?.code,
      )

      // Determine if the quantity can be adjusted
      const quantityAdjustable = itemConfigInfo.isQuantityAdjustable || false

      // Get the minimum incremental quantity and the max quantity allowed
      const minIncrementalQuantity = itemConfigInfo?.minQuantityIncrement || 1
      const maxQuantity = itemConfigInfo?.maxQuantityByPlan?.[selectedPlan]

      // get the display multiplier the user will see in the side of the quantity
      const displayMultiplier = itemConfigInfo?.displayMultiplier

      // parse child addons if needed
      let children = []
      if (
        itemConfigInfo.childrenNames &&
        itemConfigInfo.childrenNames.length > 0
      ) {
        children = itemConfigInfo.childrenNames.map(childName => {
          const childAddon = addonsData?.addOns.find(a => a.name === childName)
          if (!childAddon) return null

          return parseAddon(childAddon)
        })

        // deactivate child items if the father one is not selected
        if (!!selectedItem === false) {
          selectedItems.forEach(item => {
            if (children.some(child => child?.code === item.addOnCode)) {
              dispatch({type: 'REMOVE', payload: {addOnCode: item.addOnCode}})
            }
          })
        }
      }

      // handlers for the order controls (checkbox, quantity adjusters, etc)
      const orderControlFunctions = {
        onCheck: event =>
          handleCheckOnItem({
            event,
            summaryInfo,
          }),
        onIncrement: () =>
          handleIncrement(
            selectedItem?.addOnCode,
            addOn.name,
            selectedItem?.quantity,
          ),
        onDecrement: () =>
          handleDecrement(
            selectedItem?.addOnCode,
            addOn.name,
            selectedItem?.quantity,
          ),
        onQuantityInputBlur: value =>
          handleQuantityChange(selectedItem.addOnCode, value, addOn.name),
        onQuantityInputChange: value =>
          dispatch({
            type: 'UPDATE_QUANTITY',
            payload: {
              addOnCode: selectedItem.addOnCode,
              quantity: value,
            },
          }),
      }

      return {
        ...addOn,
        summaryInfo,
        formattedPrice: `${
          lastCountrySearchedRef.current.currency
        } $${selectedCurrencyInfo?.unitAmount.toFixed(2)}
            /${billingPreference} ${
          selectedCurrencyInfo?.setupFee
            ? `(+${selectedCurrencyInfo?.setupFee} setup fee)`
            : ''
        }`,
        addonCurrency,
        children,
        isDefaultAdded,
        isIncompatible,
        selectedItem,
        quantityAdjustable,
        minIncrementalQuantity,
        maxQuantity,
        displayMultiplier,
        code: addonCurrency?.code,
        orderControlFunctions,
      }
    },
    [addonsData, billingPreference, selectedItems, selectedPlan],
  )

  const parsedItemsData = useMemo(
    () => ({
      addOns:
        addonsData?.addOns
          .filter(addOn => {
            const addonInfo = addOn[billingPreference][0]

            let isAddonAvailableInTheSelectedCountry = true

            if (addonInfo) {
              isAddonAvailableInTheSelectedCountry = addonInfo.currencies.some(
                el => el.currency === lastCountrySearchedRef.current.currency,
              )
            }

            return addonInfo && isAddonAvailableInTheSelectedCountry
          })
          ?.map(addOn => parseAddon(addOn)) || [],

      oneTimeAddOns:
        addonsData?.oneTimeAddOns
          .filter(oneTimeAddOns => {
            const addonInfo = oneTimeAddOns.days

            let isAddonAvailableInTheSelectedCountry = true

            if (addonInfo) {
              isAddonAvailableInTheSelectedCountry = addonInfo.currencies.some(
                el => el.currency === lastCountrySearchedRef.current.currency,
              )
            }

            return addonInfo && isAddonAvailableInTheSelectedCountry
          })
          .map(oneTimeAddon => {
            const selectedCurrencyInfo = oneTimeAddon.days?.currencies.find(
              currency =>
                currency.currency === lastCountrySearchedRef.current.currency,
            )

            const minQuantityIncrement =
              ITEMS_CONFIG?.[oneTimeAddon.name]?.minQuantityIncrement || 1

            const summaryInfo = {
              addOnCode: oneTimeAddon?.days?.code,
              name: oneTimeAddon.name,
              quantity: minQuantityIncrement,
              unitAmount: selectedCurrencyInfo?.unitAmount,
            }

            return {
              ...oneTimeAddon,
              summaryInfo,
              code: oneTimeAddon?.days?.code,
              formattedPrice: `${
                lastCountrySearchedRef.current.currency
              } $${selectedCurrencyInfo?.unitAmount.toFixed(2)} ${
                selectedCurrencyInfo?.setupFee
                  ? `(+$${selectedCurrencyInfo?.setupFee} setup fee)`
                  : ''
              }`,
            }
          }) || [],
    }),
    [addonsData, billingPreference, parseAddon],
  )

  const logPdfDownload = async payload => {
    try {
      await client('cc/order/log-calculator-invoice', {
        data: payload,
      })
      return {success: true}
    } catch (err) {
      console.error('Error persisting mock PDF log:', err)
      throw err
    }
  }

  const handleDownloadInvoice = async () => {
    if (!printableElementRef.current) {
      toast.error('Invoice preview is not ready yet. Please try again.')
      return
    }

    try {
      setIsDownloadingInvoice(true)
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = 5
      const itemsPerPage = 8

      // Calculate total pages needed
      const totalPages = Math.ceil(selectedItems.length / itemsPerPage) || 1

      // Create a temporary container to hold page content
      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'absolute'
      tempContainer.style.left = '-9999px'
      tempContainer.style.width = '800px'
      document.body.appendChild(tempContainer)
      // Keep track of any React-mounted footer containers so we can unmount
      // them before removing the temp container.
      const mountedFooterContainers = []

      try {
        // Split content into pages
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          if (pageNum > 1) {
            pdf.addPage()
          }

          // Clone the original invoice element for this page
          const pageElement = printableElementRef.current.cloneNode(true)

          // Adjust styles for PDF
          pageElement.style.margin = '0'
          pageElement.style.padding = '20px'
          pageElement.style.boxShadow = 'none'
          pageElement.style.border = 'none'
          pageElement.style.borderRadius = '0'

          tempContainer.innerHTML = ''
          tempContainer.appendChild(pageElement)

          // Find the table body and limit items for this page.
          // Only treat product rows as pageable (avoid counting non-product rows
          // like totals or other sections). Product rows contain a "Code:" label
          // in the first cell, so filter by that to compute slicing indices.
          const tableBody = pageElement.querySelector('tbody')
          if (tableBody) {
            const allRows = Array.from(tableBody.querySelectorAll('tr'))

            // Identify which rows correspond to products. Prefer explicit
            // data attributes or ids (added to the printable invoice) and
            // fall back to checking for the "Code:" text in a descendant
            // div for backwards compatibility.
            const productRows = allRows.filter(row => {
              const codeDiv = row.querySelector(
                '[data-product-code], [data-code], #product-code',
              )
              if (codeDiv) return true

              const div = row.querySelector('div')
              return div && (div.textContent || '').includes('Code:')
            })

            const startIdx = (pageNum - 1) * itemsPerPage
            const endIdx = Math.min(startIdx + itemsPerPage, productRows.length)

            // Remove only the product rows that are not on this page. Keep any
            // non-product rows (if present) so page structure remains valid.
            productRows.forEach((row, idx) => {
              if (idx < startIdx || idx >= endIdx) {
                row.remove()
              }
            })
          }

          // Hide summary and coupons sections on non-last pages.
          // Prefer selecting by IDs/data-attributes if available, with a
          // fallback to the original textContent checks for backwards
          // compatibility with existing markup.
          if (pageNum !== totalPages) {
            // Try using explicit selectors first (IDs or data-attributes).
            const totalsSelectors = [
              '#invoice-subtotal',
              '#invoice-discounts',
              '#invoice-total-due-today',
              '#invoice-next-month-total',
              '[data-role="discounts"]',
              '#prorated-estimate',
            ]

            let foundAny = false
            totalsSelectors.forEach(sel => {
              const el = pageElement.querySelector(sel)
              if (el) {
                foundAny = true
                const parent = el.closest('div') || el
                try {
                  parent.style.display = 'none'
                } catch (e) {
                  // ignore style errors
                }
              }
            })

            // Fallback: if no elements matched the explicit selectors,
            // fall back to the previous content-based approach.
            if (!foundAny) {
              const allDivs = Array.from(pageElement.querySelectorAll('div'))
              allDivs.forEach(div => {
                // Fallback: hide known elements by ID or data-role instead of using text content.
                const idFallbackSelectors = [
                  '#invoice-subtotal',
                  '#invoice-discounts',
                  '#invoice-total-due-today',
                  '#invoice-next-month-total',
                  '#prorated-estimate',
                  '[data-role="discounts"]',
                  '[data-role="prorated-note"]',
                  '#applied-discounts',
                ]

                idFallbackSelectors.forEach(sel => {
                  const el = pageElement.querySelector(sel)
                  if (el) {
                    const parent = el.closest('div') || el
                    if (parent) {
                      parent.style.display = 'none'
                    }
                  }
                })
              })
            }

            // Hide coupons section: prefer an explicit container id or data
            // attribute, otherwise fall back to heading text.
            const couponsSection =
              pageElement.querySelector('#applied-discounts') ||
              pageElement.querySelector('[data-section="applied-discounts"]')

            if (couponsSection) {
              try {
                couponsSection.style.display = 'none'
              } catch (e) {
                // ignore
              }
            } else {
              const allH4s = Array.from(pageElement.querySelectorAll('h4'))
              allH4s.forEach(h4 => {
                if (h4.textContent?.includes('Applied Discounts')) {
                  const parent = h4.closest('div')
                  if (parent) {
                    parent.style.display = 'none'
                  }
                }
              })
            }

            // Ensure the prorated-estimate footer is present on every page.
            // Hide any existing footer instances. Prefer explicit selector
            // then fall back to text search.
            let existingFooters = Array.from(
              pageElement.querySelectorAll(
                '#prorated-estimate, [data-role="prorated-note"]',
              ),
            )

            if (existingFooters.length === 0) {
              existingFooters = Array.from(
                pageElement.querySelectorAll('div'),
              ).filter(d =>
                (d.textContent || '')
                  .toLowerCase()
                  .includes('prorated estimate'),
              )
            }

            // Hide existing footers only for non-last pages so they don't
            // interfere with our pagination layout.
            existingFooters.forEach(f => {
              try {
                f.style.display = 'none'
              } catch (e) {
                // ignore
              }
            })
          }

          // Capture this page with html2canvas
          const canvas = await html2canvas(pageElement, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
            width: pageElement.scrollWidth,
            height: pageElement.scrollHeight,
          })

          const imgData = canvas.toDataURL('image/png')
          const imgWidth = canvas.width
          const imgHeight = canvas.height

          // Calculate dimensions to fit on page with reduced margins
          const contentWidth = pageWidth - margin * 2
          const contentHeight = pageHeight - margin
          const ratio = Math.min(
            contentWidth / (imgWidth * 0.264583), // Convert px to mm
            contentHeight / (imgHeight * 0.264583),
          )

          const finalWidth = imgWidth * 0.264583 * ratio
          const finalHeight = imgHeight * 0.264583 * ratio

          // Center the content
          const x = (pageWidth - finalWidth) / 2
          const y = margin

          pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight)
        }
      } finally {
        mountedFooterContainers.forEach(container => {
          ReactDOM.unmountComponentAtNode(container)
        })

        if (document.body.contains(tempContainer)) {
          document.body.removeChild(tempContainer)
        }
      }

      // Render a simple footer on every page using jsPDF text APIs. This
      // avoids html layout issues and ensures proper wrapping using
      // splitTextToSize. We also reduce font size if needed to prevent
      // overflowing the available footer area.
      try {
        const footerText =
          'Please Note: Total Due Today shown is a prorated estimate and may vary based on days remaining in your billing cycle. At the time of payment, you will be shown your new monthly billing total.'

        // Determine number of pages (different jspdf builds expose different helpers)
        let pageCount
        if (typeof pdf.getNumberOfPages === 'function') {
          pageCount = pdf.getNumberOfPages()
        } else if (
          pdf.internal &&
          typeof pdf.internal.getNumberOfPages === 'function'
        ) {
          pageCount = pdf.internal.getNumberOfPages()
        } else if (pdf.internal && pdf.internal.pages) {
          pageCount = pdf.internal.pages.length
        } else {
          pageCount = 1
        }

        // We'll attempt to keep footer to at most 2 lines. Reduce font size if
        // it would wrap to more lines to avoid overlapping main content.
        for (let p = 1; p <= pageCount; p++) {
          pdf.setPage(p)

          const pageW = pdf.internal.pageSize.getWidth()
          const pageH = pdf.internal.pageSize.getHeight()
          const marginX = 10
          const maxFooterWidth = pageW - marginX * 2

          // Start with a reasonable font size and reduce if needed.
          // Use bold + blue color (#3c5199) for the footer, then restore
          // font/color after rendering so other content isn't affected.
          let fontSize = 10
          try {
            // some jspdf builds accept setFont(fontName, fontStyle)
            pdf.setFont('helvetica', 'bold')
          } catch (e) {
            /* ignore if API unavailable */
          }
          // set blue color: #3c5199 -> rgb(60,81,153)
          try {
            pdf.setTextColor(60, 81, 153)
          } catch (e) {
            /* ignore */
          }

          pdf.setFontSize(fontSize)
          let lines = pdf.splitTextToSize(footerText, maxFooterWidth)

          while (lines.length > 2 && fontSize > 6) {
            fontSize -= 1
            pdf.setFontSize(fontSize)
            lines = pdf.splitTextToSize(footerText, maxFooterWidth)
          }

          // Compute vertical placement so the footer sits above the bottom by a small gap
          const lineHeight = fontSize * 1.15 * 0.352777778 // convert pt to mm (approx)
          const footerHeight = lines.length * lineHeight
          const yPos = pageH - footerHeight - 1 // [n]mm gap from bottom

          // Draw an orange horizontal rule across the page above the footer
          // Orange chosen: #ff8a00 -> rgb(255,138,0). Keep this in a try/catch
          // so older jspdf builds without setDrawColor don't crash.
          try {
            // draw edge-to-edge across the page (ignore the content margins)
            const xStart = 0
            const xEnd = pageW
            // Add configurable padding (in mm) between the horizontal rule and
            // the top of the footer text so the text doesn't sit too close.
            const footerTextPadding = 6
            const lineY = yPos - footerTextPadding
            try {
              pdf.setLineWidth(0.8)
            } catch (e) {
              /* ignore if API missing */
            }
            try {
              pdf.setDrawColor(255, 138, 0)
            } catch (e) {
              /* ignore if API missing */
            }
            try {
              pdf.line(xStart, lineY, xEnd, lineY)
            } catch (e) {
              /* ignore drawing error */
            }
            // restore draw color
            try {
              pdf.setDrawColor(0, 0, 0)
            } catch (e) {
              /* ignore */
            }
          } catch (err) {
            /* ignore any footer-line drawing errors */
          }

          // Render centered text. Use array form to render wrapped lines.
          const xPos = pageW / 2
          pdf.text(lines, xPos, yPos, {align: 'center'})

          // Restore font and color so subsequent PDF operations remain unchanged
          try {
            pdf.setFont('helvetica', 'normal')
          } catch (e) {
            /* ignore */
          }
          try {
            pdf.setTextColor(0, 0, 0)
          } catch (e) {
            /* ignore */
          }
        }
      } catch (err) {
        console.error('Failed to render footer in PDF (text fallback):', err)
      }

      pdf.save(
        `invoice-preview-${email.split('@')[0]}-${dayjs().format(
          'YY-MM-DD-hh:mmA',
        )}.pdf`,
      )

      try {
        const totalDiscount = calculateTotalDiscount()
        const payload = {
          loggedAt: new Date().toISOString(),
          ooeUser: {
            name: user?.name || null,
            email: user?.email || null,
          },
          clientEmail: email || null,
          products: selectedItems.map(p => ({
            addOnCode: p.addOnCode,
            name: p.name,
            quantity: p.quantity,
            unitAmount: p.unitAmount,
          })),
          subtotal,
          totalDiscount,
          totalDueToday: paymentPreview?.totalDueToday || 0,
          monthlyTotal: firstMonthTotal - totalDiscount || 0,
        }

        await logPdfDownload(payload)
        toast.success('PDF download request recorded for audit')
      } catch (logError) {
        console.error('Failed to log PDF download:', logError)
        toast.error(
          'PDF downloaded but failed to record the download for audit',
        )
      }
    } catch (error) {
      console.error('Error generating PDF invoice:', error)
      toast.error('Unable to download the invoice. Please try again.')
    } finally {
      setIsDownloadingInvoice(false)
    }
  }

  return (
    <div
      data-theme="ooe-v1"
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        background: '#ffffff',
        padding: '16px',
        border: '1px solid #dde1ea',
        gap: '16px',
      }}
    >
      <form action="" onSubmit={e => e.preventDefault()}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '12px',
            maxWidth: '600px',
          }}
        >
          <div style={{flex: 1}}>
            <Input
              type="text"
              placeholder="Email Address"
              aria-label="Email Address"
              name="email"
              size="md"
              variant="form"
              leftIcon={<StyledSearchIcon />}
              onChange={e => {
                setEmail(e.target.value)
                setAccountVersion(null)
              }}
            />
          </div>
          <div style={{flexShrink: 0}}>
            <Button
              type="button"
              variant="solid-dark-blue"
              size="md"
              onClick={handleEmailValidation}
              disabled={!email?.trim() || isLoadingAccount}
            >
              Validate
            </Button>
          </div>
        </div>
      </form>
      <section
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          flex: 1,
        }}
      >
        {isLoadingAccount && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '48px 0',
              color: '#26325f',
            }}
          >
            <ButtonSpinner
              style={{width: '24px', height: '24px', borderWidth: '3px'}}
            />
          </div>
        )}
        {!isLoadingAccount && accountVersion !== 'v4.0' && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '48px 0',
            }}
          >
            <p
              style={{
                fontSize: '14px',
                color: '#6b7280',
                textAlign: 'center',
                maxWidth: '420px',
                lineHeight: '1.6',
                margin: 0,
              }}
            >
              Enter a valid account to see specific account pricing calculator
              (This calculator is only valid for Thryv 4.0 accounts)
            </p>
          </div>
        )}
        {!isLoadingAccount &&
          accountVersion !== null &&
          accountVersion !== 'v5.0' && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 2fr',
                gap: '16px',
                maxHeight: '80vh',
                gridTemplateRows: '1fr',
              }}
            >
              <div
                style={{
                  background: '#ffffff',
                  padding: '20px',
                  paddingRight: '10px',
                  borderRadius: '8px',
                  border: '1px solid #dde1ea',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  maxHeight: '100%',
                  scrollbarGutter: 'stable',
                }}
              >
                <h2
                  style={{
                    fontSize: '22px',
                    fontWeight: 600,
                    color: '#1d2433',
                    margin: '0 0 4px 0',
                  }}
                >
                  Summary
                </h2>
                <p
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#6A7181',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    margin: 0,
                    paddingBlock: '12px',
                  }}
                >
                  Coupons
                </p>
                {isLoadingAddons ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '50vh',
                      color: '#26325f',
                    }}
                  >
                    <ButtonSpinner
                      style={{
                        width: '24px',
                        height: '24px',
                        borderWidth: '3px',
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '8px',
                      }}
                    >
                      <div style={{flex: 1}}>
                        <Input
                          type="text"
                          size="md"
                          variant="form"
                          placeholder="Search by code"
                          value={couponSearchTerm}
                          onChange={e => setCouponSearchTerm(e.target.value)}
                        />
                      </div>
                      <Button
                        variant="solid-dark-blue"
                        size="md"
                        type="button"
                        onClick={handleSearchCouponCode}
                      >
                        Validate
                      </Button>
                    </div>
                    {isLoadingCoupons && (
                      <div
                        style={{width: '100%', height: '24px', margin: '8px 0'}}
                      >
                        <SkeletonLoader height="24px" width="100%" />
                      </div>
                    )}
                    {!isLoadingCoupons && coupons && coupons.length > 0 && (
                      <div style={{marginTop: '12px'}}>
                        <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                          {coupons.map(coupon => (
                            <li key={coupon.code} style={{marginBottom: '8px'}}>
                              <label
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#26325f',
                                    flexShrink: 0,
                                  }}
                                  checked={selectedCoupons.some(
                                    c => c.code === coupon.code,
                                  )}
                                  onChange={e =>
                                    handleCheckOnCoupon({event: e, coupon})
                                  }
                                />
                                <span
                                  style={{fontSize: '14px', color: '#1d2433'}}
                                >
                                  <b>{coupon.code}</b>: {coupon.description}{' '}
                                  {coupon.discountAmount > 0 && (
                                    <span
                                      style={{
                                        color: '#059669',
                                        marginLeft: '8px',
                                      }}
                                    >
                                      (-${coupon.discountAmount.toFixed(2)})
                                    </span>
                                  )}
                                  {coupon.discountPercent > 0 && (
                                    <span
                                      style={{
                                        color: '#059669',
                                        marginLeft: '8px',
                                      }}
                                    >
                                      (-{coupon.discountPercent}%)
                                    </span>
                                  )}
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div
                      style={{
                        borderTop: '1px solid #dde1ea',
                        margin: '12px 0',
                      }}
                    />

                    <ul
                      style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      {selectedItems.map(selectedItem => {
                        // Find coupons that apply to this product
                        const productCoupons = selectedCoupons.filter(
                          coupon =>
                            coupon.appliesToPlans?.includes(
                              selectedItem.addOnCode,
                            ) ||
                            coupon.appliesToItems?.includes(
                              selectedItem.addOnCode,
                            ),
                        )

                        const productTotal =
                          selectedItem.unitAmount * selectedItem.quantity

                        return (
                          <li
                            key={selectedItem.addOnCode}
                            style={{marginBottom: '12px'}}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <span
                                style={{fontSize: '13px', color: '#6A7181'}}
                              >
                                {selectedItem.name} (Qty:{' '}
                                {selectedItem.quantity})
                              </span>
                              <span
                                style={{fontSize: '13px', color: '#6A7181'}}
                              >
                                ${productTotal.toFixed(2)}
                              </span>
                            </div>

                            {/* Display coupons for this product */}
                            {productCoupons.length > 0 && (
                              <div style={{marginLeft: '20px'}}>
                                {productCoupons.map(coupon => {
                                  const discount =
                                    coupon.discountAmount > 0
                                      ? coupon.discountAmount
                                      : productTotal *
                                        (coupon.discountPercent / 100)

                                  return (
                                    <div
                                      key={coupon.code}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        color: '#059669',
                                        fontSize: '13px',
                                        margin: '2px 0',
                                      }}
                                    >
                                      <span>{coupon.description}</span>
                                      <span>-${discount.toFixed(2)}</span>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </li>
                        )
                      })}
                    </ul>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBlock: '4px',
                      }}
                    >
                      <p
                        style={{fontSize: '14px', color: '#1d2433', margin: 0}}
                      >
                        <strong>Subtotal:</strong>
                      </p>
                      <p
                        style={{fontSize: '14px', color: '#1d2433', margin: 0}}
                      >
                        <strong>${subtotal.toFixed(2)}</strong>
                      </p>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBlock: '4px',
                      }}
                    >
                      <p
                        style={{fontSize: '14px', color: '#f76800', margin: 0}}
                      >
                        <strong>Total Discount:</strong>
                      </p>
                      <p
                        style={{fontSize: '14px', color: '#f76800', margin: 0}}
                      >
                        <strong>
                          -{formatPrice(calculateTotalDiscount())}
                        </strong>
                      </p>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBlock: '4px',
                      }}
                    >
                      <p
                        style={{fontSize: '14px', color: '#1d2433', margin: 0}}
                      >
                        <strong>Next Month Total:</strong>
                      </p>
                      <p
                        style={{fontSize: '14px', color: '#1d2433', margin: 0}}
                      >
                        <strong>
                          {formatPrice(
                            firstMonthTotal - calculateTotalDiscount(),
                          )}
                        </strong>
                      </p>
                    </div>

                    <div
                      style={{
                        borderTop: '1px solid #dde1ea',
                        margin: '8px 0',
                      }}
                    />

                    {isLoadingPaymentPreview && lineItemsToLoad.length > 0 && (
                      <div>
                        {lineItemsToLoad.map(item => (
                          <div key={item.planCode} className="mb-1">
                            <SkeletonLoader height="24px" width="100%" />
                          </div>
                        ))}
                      </div>
                    )}
                    {paymentPreview && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          flexDirection: 'column',
                          marginTop: '18px',
                        }}
                      >
                        {paymentPreview.lineItems.map(item => (
                          <div
                            key={item.planCode}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <span style={{fontSize: '13px', color: '#6A7181'}}>
                              {item.description} (Qty: {item.quantity})
                            </span>
                            <span style={{fontSize: '13px', color: '#6A7181'}}>
                              ${item.amount.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: '16px',
                      }}
                    >
                      <p
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#1d2433',
                          margin: 0,
                        }}
                      >
                        <strong>Total Due Today:</strong>
                      </p>
                      <p
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#1d2433',
                          margin: 0,
                        }}
                      >
                        <strong>
                          {isLoadingPaymentPreview ? (
                            <SkeletonLoader
                              height="24px"
                              width="100px"
                            ></SkeletonLoader>
                          ) : (
                            `$${(paymentPreview?.totalDueToday || 0).toFixed(
                              2,
                            )}`
                          )}
                        </strong>
                      </p>
                    </div>
                    <div
                      style={{
                        marginTop: '12px',
                        background: '#26325f',
                        color: '#ffffff',
                        fontWeight: 600,
                        padding: '10px 12px',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    >
                      <span>
                        Total Due Today shown is a prorated estimate and may
                        vary based on days remaining in your billing cycle. At
                        the time of payment, you will be shown your new monthly
                        billing total.
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: '16px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        type="button"
                        variant="solid-dark-blue"
                        onClick={() => setIsInvoiceModalOpen(true)}
                        disabled={selectedItems.length === 0}
                      >
                        View Invoice
                      </Button>
                    </div>
                  </>
                )}
              </div>
              {/* calculator options */}
              <div
                style={{
                  background: '#ffffff',
                  padding: '20px',
                  border: '1px solid #dde1ea',
                  maxHeight: '100%',
                  overflowY: 'auto',
                  borderRadius: '8px',
                  scrollbarGutter: 'stable',
                }}
              >
                {accountVersion === 'v4.0' && (
                  <div className="flex flex-col">
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '10px',
                      }}
                    >
                      {planCodeNames.map(plan => {
                        const planPrice = plansPricesInfoRef.current?.find(
                          planInfo => planInfo.name === plan,
                        )?.amount

                        return (
                          <Button
                            key={plan}
                            onClick={() => handleLoadPlan(plan)}
                            type="button"
                            name={plan}
                            style={{
                              width: '100%',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              height: '56px',
                            }}
                            disabled={
                              isLoadingCoupons ||
                              isLoadingPaymentPreview ||
                              isLoadingAddons
                            }
                            variant={
                              selectedPlan === plan
                                ? 'solid-dark-blue'
                                : 'secondary'
                            }
                          >
                            <span
                              style={{
                                alignItems: 'center',
                                gap: '8px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <b>
                                {plan.charAt(0).toUpperCase() + plan.slice(1)}
                              </b>
                              <br />
                              <span style={{fontWeight: 400}}>
                                {lastCountrySearchedRef.current.currency} $
                                {planPrice}
                              </span>
                            </span>
                          </Button>
                        )
                      })}
                    </div>
                    {/* Loading indicator for addons */}
                    {isLoadingAddons && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          marginTop: '10px',
                          minHeight: '50vh',
                          color: '#26325f',
                        }}
                      >
                        <ButtonSpinner
                          style={{
                            width: '24px',
                            height: '24px',
                            borderWidth: '3px',
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Render addons data */}
                {!isLoadingAddons && selectedPlan && parsedItemsData && (
                  <div style={{marginTop: '20px'}}>
                    <h3
                      style={{
                        fontSize: '20px',
                        fontWeight: 600,
                        color: '#1d2433',
                        margin: '0 0 2px 0',
                      }}
                    >
                      {selectedPlan &&
                        `${
                          selectedPlan.charAt(0).toUpperCase() +
                          selectedPlan.slice(1)
                        } Plan Addons`}
                    </h3>

                    {/* Regular Add-ons Section */}
                    {parsedItemsData.addOns &&
                      parsedItemsData.addOns.length > 0 && (
                        <div style={{marginBottom: '20px'}}>
                          <h4
                            style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: '#f76800',
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                              margin: '0 0 16px 0',
                            }}
                          >
                            Add-ons
                          </h4>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                            }}
                          >
                            {parsedItemsData.addOns
                              .filter(addon =>
                                isAddonVisibleOnSelectedPlan(
                                  addon.name,
                                  selectedPlan,
                                ),
                              )
                              .sort(sortAddons)
                              .map((addOn, index) => {
                                return (
                                  <AddonItem
                                    key={addOn.addonCurrency?.code || index}
                                    addon={addOn}
                                    isSelected={!!addOn.selectedItem}
                                    isDeactivated={addOn.isIncompatible}
                                    isCheckboxDisabled={addOn.isDefaultAdded}
                                    quantity={addOn.selectedItem?.quantity}
                                    childrenAddonItems={addOn.children}
                                    isQuantityAdjustable={
                                      addOn.quantityAdjustable
                                    }
                                    minQuantity={addOn.minIncrementalQuantity}
                                    maxQuantity={addOn.maxQuantity}
                                    displayMultiplier={addOn.displayMultiplier}
                                    showPrice={true}
                                    onCheck={
                                      addOn.orderControlFunctions.onCheck
                                    }
                                    onIncrement={
                                      addOn.orderControlFunctions.onIncrement
                                    }
                                    onDecrement={
                                      addOn.orderControlFunctions.onDecrement
                                    }
                                    onQuantityInputBlur={
                                      addOn.orderControlFunctions
                                        .onQuantityInputBlur
                                    }
                                    onQuantityInputChange={
                                      addOn.orderControlFunctions
                                        .onQuantityInputChange
                                    }
                                  />
                                )
                              })}
                          </div>
                        </div>
                      )}

                    {/* One-Time Add-ons Section */}
                    {parsedItemsData.oneTimeAddOns &&
                      parsedItemsData.oneTimeAddOns.length > 0 && (
                        <div style={{marginBottom: '20px'}}>
                          <h4
                            className="text-thryv-orange-500"
                            style={{marginBottom: '10px', marginTop: '30px'}}
                          >
                            One-Time Add-ons
                          </h4>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            {parsedItemsData.oneTimeAddOns
                              .filter(addon =>
                                isAddonVisibleOnSelectedPlan(
                                  addon.name,
                                  selectedPlan,
                                ),
                              )
                              .sort(sortAddons)
                              .map((addon, index) => {
                                const defaultProduct = isAddonDefaultAdded(
                                  addon?.code,
                                )
                                const isDefaultAdded =
                                  !!defaultProduct?.isDefault
                                const selectedItem = selectedItems.find(
                                  item => item.addOnCode === addon.code,
                                )

                                return (
                                  <AddonItem
                                    key={addon.code || index}
                                    addon={addon}
                                    isSelected={!!selectedItem}
                                    isCheckboxDisabled={isDefaultAdded}
                                    isQuantityAdjustable={
                                      (isQuantityAdjustable(addon.name) ||
                                        isDefaultAdded) &&
                                      !!selectedItem
                                    }
                                    quantity={selectedItem?.quantity}
                                    minQuantity={
                                      ITEMS_CONFIG?.[addon.name]
                                        ?.minQuantityIncrement || 1
                                    }
                                    maxQuantity={
                                      ITEMS_CONFIG?.[addon.name]
                                        ?.maxQuantityByPlan?.[selectedPlan]
                                    }
                                    displayMultiplier={
                                      ITEMS_CONFIG?.[addon.name]
                                        ?.displayMultiplier
                                    }
                                    showPrice={true}
                                    onCheck={event =>
                                      handleCheckOnItem({
                                        event,
                                        summaryInfo: addon.summaryInfo,
                                      })
                                    }
                                    onIncrement={() =>
                                      handleIncrement(
                                        selectedItem.addOnCode,
                                        addon.name,
                                        selectedItem.quantity,
                                      )
                                    }
                                    onDecrement={() =>
                                      handleDecrement(
                                        selectedItem.addOnCode,
                                        addon.name,
                                        selectedItem.quantity,
                                      )
                                    }
                                    onQuantityInputBlur={value =>
                                      handleQuantityChange(
                                        selectedItem.addOnCode,
                                        value,
                                        addon.name,
                                      )
                                    }
                                    onQuantityInputChange={value =>
                                      dispatch({
                                        type: 'UPDATE_QUANTITY',
                                        payload: {
                                          addOnCode: selectedItem.addOnCode,
                                          quantity: value,
                                        },
                                      })
                                    }
                                  />
                                )
                              })}
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          )}
      </section>
      {isInvoiceModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '24px',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              width: 'min(920px, 100%)',
              maxHeight: '95vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.25)',
            }}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h3 style={{margin: 0}}>Invoice Preview</h3>
              <button
                onClick={() => setIsInvoiceModalOpen(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
                aria-label="Close invoice preview"
              >
                ×
              </button>
            </div>

            <div
              style={{
                padding: '24px',
                overflowY: 'auto',
              }}
            >
              <PrintableInvoice
                printableElementRef={printableElementRef}
                selectedItems={selectedItems}
                selectedCoupons={selectedCoupons}
                subtotal={subtotal}
                totalDiscount={calculateTotalDiscount()}
                totalDueToday={paymentPreview?.totalDueToday || 0}
                paymentPreviewLineItems={paymentPreview?.lineItems || []}
                nextMonthTotal={firstMonthTotal - calculateTotalDiscount()}
                currency={lastCountrySearchedRef.current?.currency || 'USD'}
                customerEmail={email}
                userName={user?.name || ''}
                userEmail={user?.email || ''}
                userOnboardingFeeItem={
                  userOnboardingFeeItem
                    ? {
                        addOnCode: 'thryvprfee',
                        name:
                          userOnboardingFeeItem.description || 'Onboarding Fee',
                        quantity: userOnboardingFeeItem.quantity || 1,
                        unitAmount: userOnboardingFeeItem.amount || 0,
                      }
                    : null
                }
              />
            </div>
            <div
              id="prorated-estimate"
              data-role="prorated-note"
              style={{
                marginTop: '12px',
                background: '#3c5199',
                color: '#ffffff',
                fontWeight: 600,
                padding: '10px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                maxWidth: '100%',
                marginInline: '24px',
              }}
            >
              <span>
                Please Note: Total Due Today shown is a prorated estimate and
                may vary based on days remaining in your billing cycle. At the
                time of payment, you will be shown your new monthly billing
                total.
              </span>
            </div>
            <div
              style={{
                padding: '16px 24px 24px',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                borderTop: '1px solid #f0f0f0',
              }}
            >
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsInvoiceModalOpen(false)}
              >
                Close
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleDownloadInvoice}
                disabled={isDownloadingInvoice}
              >
                {isDownloadingInvoice ? 'Preparing PDF…' : 'Download PDF'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const PrintableInvoice = ({
  printableElementRef,
  selectedItems = [],
  selectedCoupons = [],
  subtotal = 0,
  totalDiscount = 0,
  totalDueToday = 0,
  paymentPreviewLineItems = [],
  nextMonthTotal = 0,
  currency = 'USD',
  customerEmail = '',
  userName = '',
  userEmail = '',
  userOnboardingFeeItem = null,
}) => {
  const accentOrange = '#ff5000'
  const accentIndigo = '#3c5199'
  const hasItems = selectedItems.length > 0

  const selectedItemsWithOnboardingFee = userOnboardingFeeItem
    ? [...selectedItems, userOnboardingFeeItem]
    : selectedItems

  // Get date/time with timezone
  const getFormattedDateTime = () => {
    const now = new Date()
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const dateTimeString = now.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      timeZone: timeZone,
    })
    return dateTimeString
  }

  const formattedDateTime = getFormattedDateTime()
  const paymentPreviewLineItemsMap = (paymentPreviewLineItems || []).reduce(
    (acc, li) => {
      if (li && li.planCode) {
        acc[li.planCode] = li.amount
        // Map onboarding preview code to the onboarding fee product code
        if (li.planCode === 'onboarding-fee') {
          acc['thryvprfee'] = li.amount
        }
      }
      return acc
    },
    {},
  )
  const formatCurrency = (amount, quantity = 1, title = '') =>
    `${currency} ${formatPrice(amount, quantity, title)}`
  const getCouponDiscount = coupon => {
    if (coupon.discountAmount > 0) return coupon.discountAmount

    if (coupon.discountPercent > 0) {
      const applicableTotal = selectedItems.reduce((total, item) => {
        const applies =
          coupon.appliesToAllPlans ||
          coupon.appliesToPlans?.includes(item.addOnCode) ||
          coupon.appliesToItems?.includes(item.addOnCode)

        if (!applies) return total
        return total + item.unitAmount * item.quantity
      }, 0)

      return applicableTotal * (coupon.discountPercent / 100)
    }

    return 0
  }

  return (
    <div
      ref={printableElementRef}
      style={{
        fontFamily: 'Open Sans, sans-serif',
        backgroundColor: '#ffffff',
        color: '#1f2933',
        borderRadius: '18px',
        padding: '32px',
        border: `1px solid ${accentIndigo}22`,
        boxShadow: '0 24px 60px rgba(60, 81, 153, 0.15)',
        minWidth: '600px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <img
            src={thryvLogo}
            alt="Thryv Logo"
            style={{width: '170px', height: 'auto'}}
          />
        </div>
        <div style={{textAlign: 'right'}}>
          <h2
            style={{
              margin: 0,
              color: accentIndigo,
              letterSpacing: '0.02em',
              textTransform: 'none',
              fontSize: '20px',
              fontWeight: 700,
            }}
          >
            Invoice Preview
          </h2>
          {userName && userEmail && (
            <p
              style={{
                margin: '8px 0 0',
                color: '#334155',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              Prepared by: {userName}, {userEmail}
            </p>
          )}
          <p style={{margin: '6px 0 0', color: '#64748b', fontSize: '13px'}}>
            Customer: {customerEmail || 'Unassigned customer'}
          </p>
          <p style={{margin: '6px 0 0', color: '#64748b', fontSize: '12px'}}>
            {formattedDateTime}
          </p>
        </div>
      </div>

      <div
        style={{
          marginBottom: '24px',
        }}
      >
        <h4
          style={{
            color: accentOrange,
            verticalAlign: 'middle',
          }}
        >
          Summary
        </h4>
      </div>

      {hasItems ? (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: '24px',
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: `${accentIndigo}0f`,
                color: accentIndigo,
                textAlign: 'left',
              }}
            >
              <th style={{paddingBlock: '12px', paddingLeft: '12px'}}>
                Product
              </th>
              <th style={{paddingBlock: '12px', paddingLeft: '12px'}}>Qty</th>
              <th style={{paddingBlock: '12px', paddingLeft: '12px'}}>
                Due Today
              </th>
              <th
                style={{
                  paddingBlock: '12px',
                  paddingInline: '12px',
                  textAlign: 'right',
                }}
              >
                Due Monthly
              </th>
            </tr>
          </thead>
          <tbody>
            {selectedItemsWithOnboardingFee.map(item => (
              <tr
                key={item.addOnCode}
                style={{borderBottom: '1px solid #e5e7eb'}}
              >
                <td style={{padding: '8px 12px'}}>
                  <strong>{item.name}</strong>
                  <div
                    data-product-code={item.addOnCode}
                    id={`product-code-${item.addOnCode}`}
                    style={{color: '#6b7280', fontSize: '12px'}}
                  >
                    Code: {item.addOnCode}
                  </div>
                </td>
                <td style={{padding: '8px 12px'}}>{item.quantity}</td>
                <td style={{padding: '8px 12px'}}>
                  {formatCurrency(
                    paymentPreviewLineItemsMap[item.addOnCode] || 0,
                  )}
                </td>
                <td style={{padding: '8px 12px', textAlign: 'right'}}>
                  {item.addOnCode === 'thryvprfee'
                    ? formatCurrency(0)
                    : formatCurrency(item.unitAmount, item.quantity, item.name)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={{color: '#6b7280'}}>No products selected yet.</p>
      )}

      {selectedCoupons.length > 0 && (
        <div id="applied-discounts" style={{marginBottom: '24px'}}>
          <div
            style={{
              marginBottom: '24px',
            }}
          >
            <h4
              style={{
                color: accentOrange,
                verticalAlign: 'middle',
              }}
            >
              Applied Discounts
            </h4>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
            {selectedCoupons.map(coupon => {
              const discountAmount = getCouponDiscount(coupon)
              return (
                <div
                  key={coupon.code}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingBottom: '4px',
                    fontWeight: 600,
                  }}
                >
                  <span>
                    {coupon.code} — {coupon.description}
                  </span>
                  <span style={{color: accentOrange}}>
                    -
                    {formatCurrency(
                      discountAmount || coupon.discountAmount || 0,
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div
        style={{
          borderTop: `2px solid ${accentIndigo}15`,
          paddingTop: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <div
          id="invoice-subtotal"
          style={{display: 'flex', justifyContent: 'space-between'}}
        >
          <span style={{color: '#6b7280'}}>Subtotal</span>
          <strong>{formatCurrency(subtotal)}</strong>
        </div>
        <div
          id="invoice-discounts"
          style={{display: 'flex', justifyContent: 'space-between'}}
        >
          <span style={{color: '#6b7280'}}>Discounts</span>
          <strong style={{color: accentOrange}}>
            -{formatCurrency(totalDiscount)}
          </strong>
        </div>
        <div
          id="invoice-next-month-total"
          style={{display: 'flex', justifyContent: 'space-between'}}
        >
          <span style={{color: '#6b7280'}}>Next Month Total</span>
          <strong>{formatCurrency(nextMonthTotal)}</strong>
        </div>
        <div
          id="invoice-total-due-today"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: `1px solid #e6e9ef`,
            paddingTop: '10px',
            color: '#0f172a',
            marginTop: '6px',
          }}
        >
          <span style={{fontWeight: 600}}>Total Due Today</span>
          <strong style={{fontSize: '16px'}}>
            {formatCurrency(totalDueToday)}
          </strong>
        </div>
      </div>
    </div>
  )
}

export {PriceCalculatorScreen}
